import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Copy, Users, Link2, ClipboardList, KeyRound, Archive, ArchiveRestore, Plus, X, Download, Trash2 } from "lucide-react";
import { fmtDate } from "../lib/status";

export default function Manage() {
  const [tab, setTab] = useState("users");
  const [exporting, setExporting] = useState(false);

  const doExport = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().slice(0, 10);
      a.href = url;
      a.download = `morgan-shop-tracker-export-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.fleet.length} units, ${data.inventory.length} items, ${data.audit_log.length} audit entries`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-4" data-testid="manage-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Manage</h1>
          <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mt-1">Users · Invites · Audit Log · Backup</p>
        </div>
        <button onClick={doExport} disabled={exporting} data-testid="export-all-btn"
          className="text-xs font-mono uppercase tracking-wider px-3 py-2 border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 text-red-200 rounded-sm transition-colors flex items-center gap-2 disabled:opacity-50">
          <Download className="h-3.5 w-3.5" />
          {exporting ? "Exporting…" : "Export All Data (JSON)"}
        </button>
      </div>
      <div className="flex gap-2 border-b border-zinc-800">
        {[
          { k: "users", label: "Users", icon: Users },
          { k: "invites", label: "Invites", icon: Link2 },
          { k: "audit", label: "Audit Log", icon: ClipboardList },
        ].map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`manage-tab-${t.k}`}
            className={`px-4 py-2 text-sm font-medium transition-colors flex items-center gap-2 -mb-px ${
              tab === t.k ? "border-b-2 border-red-500 text-zinc-100" : "text-zinc-500 hover:text-zinc-200 border-b-2 border-transparent"
            }`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>
      {tab === "users" && <UsersPanel />}
      {tab === "invites" && <InvitesPanel />}
      {tab === "audit" && <AuditPanel />}
    </div>
  );
}

function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [reset, setReset] = useState(null); // {username, temp_password}

  const load = async () => setUsers((await api.get("/users")).data);
  useEffect(() => { load(); }, []);

  const doReset = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`, {});
      setReset({ username: data.username, temp_password: data.temp_password });
      toast.success("Password reset");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleArchive = async (u) => {
    try {
      if (u.archived) await api.post(`/users/${u.id}/unarchive`);
      else await api.post(`/users/${u.id}/archive`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="border border-zinc-800 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-zinc-500 uppercase tracking-wider text-xs">
            <tr>
              <th className="text-left px-4 py-2">Username</th>
              <th className="text-left px-4 py-2">Role</th>
              <th className="text-left px-4 py-2 hidden sm:table-cell">Created</th>
              <th className="text-right px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {users.map((u) => (
              <tr key={u.id} className={u.archived ? "opacity-60" : ""} data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-2 font-mono text-zinc-100">{u.username}</td>
                <td className="px-4 py-2 text-zinc-300">
                  <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border ${u.role === "admin" ? "border-red-500/40 text-red-300 bg-red-500/10" : "border-zinc-700 text-zinc-400"}`}>{u.role}</span>
                </td>
                <td className="px-4 py-2 text-zinc-500 text-xs font-mono hidden sm:table-cell">{fmtDate(u.created_at)}</td>
                <td className="px-4 py-2 text-right space-x-2">
                  <button onClick={() => doReset(u)} data-testid={`user-reset-${u.id}`}
                    className="text-xs font-mono uppercase px-2 py-1 border border-zinc-700 hover:bg-zinc-800 rounded-sm transition-colors inline-flex items-center gap-1">
                    <KeyRound className="h-3 w-3" /> Reset
                  </button>
                  <button onClick={() => toggleArchive(u)} data-testid={`user-archive-${u.id}`}
                    className="text-xs font-mono uppercase px-2 py-1 border border-zinc-700 hover:bg-zinc-800 rounded-sm transition-colors inline-flex items-center gap-1">
                    {u.archived ? <><ArchiveRestore className="h-3 w-3" /> Restore</> : <><Archive className="h-3 w-3" /> Archive</>}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {reset && (
        <div className="border border-amber-500/40 bg-amber-500/10 rounded-sm p-4 relative">
          <button onClick={() => setReset(null)} className="absolute top-2 right-2 text-zinc-500 hover:text-zinc-200"><X className="h-4 w-4" /></button>
          <div className="text-sm font-semibold text-amber-300">Temporary password for {reset.username}</div>
          <div className="mt-2 flex items-center gap-2">
            <code className="font-mono bg-zinc-950 border border-zinc-800 px-3 py-1.5 rounded-sm text-zinc-100" data-testid="reset-temp-password">{reset.temp_password}</code>
            <button onClick={() => { navigator.clipboard.writeText(reset.temp_password); toast.success("Copied"); }}
              className="text-xs font-mono uppercase px-2 py-1 border border-zinc-700 hover:bg-zinc-800 rounded-sm inline-flex items-center gap-1"><Copy className="h-3 w-3" />Copy</button>
          </div>
          <div className="text-xs text-zinc-400 mt-2">Share this password with the user securely. They'll be prompted to change it after login.</div>
        </div>
      )}
    </div>
  );
}

function InvitesPanel() {
  const [invites, setInvites] = useState([]);
  const [role, setRole] = useState("user");
  const [note, setNote] = useState("");

  const load = async () => setInvites((await api.get("/invites")).data);
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/invites", { role, note: note || null });
      setNote("");
      load();
      toast.success("Invite created");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const revoke = async (id) => {
    try { await api.delete(`/invites/${id}`); load(); toast.success("Revoked"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const inviteUrl = (token) => `${window.location.origin}/invite/${token}`;

  return (
    <div className="space-y-4">
      <div className="border border-zinc-800 bg-zinc-900 rounded-sm p-4">
        <div className="text-sm font-semibold mb-3">Generate Invite Link</div>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="invite-role"
              className="bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-1.5 font-mono text-sm outline-none">
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">For (who is this invite for?)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Kenzie, John the mechanic, Rick — driver…" data-testid="invite-note"
              className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-1.5 font-mono text-sm outline-none" />
          </div>
          <button onClick={create} data-testid="invite-create"
            className="text-xs font-mono uppercase tracking-wider px-3 py-2 bg-red-600 hover:bg-red-500 rounded-sm transition-colors flex items-center gap-1.5">
            <Plus className="h-3 w-3" /> Create
          </button>
        </div>
      </div>

      <div className="border border-zinc-800 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/60 text-zinc-500 uppercase tracking-wider text-xs">
            <tr>
              <th className="text-left px-4 py-2">For / Link</th>
              <th className="text-left px-4 py-2 hidden sm:table-cell">Role</th>
              <th className="text-left px-4 py-2 hidden md:table-cell">Created</th>
              <th className="text-left px-4 py-2 hidden md:table-cell">Expires</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-right px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-800">
            {invites.map((i) => {
              const expired = new Date(i.expires_at) < new Date();
              const status = i.used ? (i.revoked ? "revoked" : "used") : expired ? "expired" : "active";
              const url = inviteUrl(i.token);
              return (
                <tr key={i.id} data-testid={`invite-row-${i.id}`}>
                  <td className="px-4 py-2 max-w-[260px] sm:max-w-[340px]">
                    <div className="text-sm text-zinc-100 font-medium truncate" data-testid={`invite-for-${i.id}`}>
                      {i.note || <span className="text-zinc-500 italic font-normal">(no name entered)</span>}
                    </div>
                    <div className="font-mono text-[10px] text-zinc-500 truncate mt-0.5">{url}</div>
                    <div className="text-[10px] font-mono text-zinc-600 mt-0.5">Created by {i.created_by_username || "—"}</div>
                  </td>
                  <td className="px-4 py-2 hidden sm:table-cell text-zinc-400 capitalize">{i.role}</td>
                  <td className="px-4 py-2 hidden md:table-cell text-zinc-500 text-xs font-mono">{fmtDate(i.created_at)}</td>
                  <td className="px-4 py-2 hidden md:table-cell text-zinc-500 text-xs font-mono">{fmtDate(i.expires_at)}</td>
                  <td className="px-4 py-2">
                    <span className={`text-[10px] font-mono uppercase px-2 py-0.5 border ${
                      status === "active" ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" :
                      status === "used" ? "border-zinc-700 text-zinc-400" :
                      "border-red-500/40 text-red-300 bg-red-500/10"
                    }`}>{status}</span>
                    {i.used && i.used_by_username && <div className="text-[10px] text-zinc-500 mt-0.5">used by {i.used_by_username}</div>}
                  </td>
                  <td className="px-4 py-2 text-right space-x-2 whitespace-nowrap">
                    <button onClick={() => { navigator.clipboard.writeText(url); toast.success(i.note ? `Link for ${i.note} copied` : "Link copied"); }} data-testid={`invite-copy-${i.id}`}
                      className="text-xs font-mono uppercase px-2 py-1 border border-zinc-700 hover:bg-zinc-800 rounded-sm inline-flex items-center gap-1">
                      <Copy className="h-3 w-3" /> Copy
                    </button>
                    <button onClick={async () => {
                      const who = i.note || `${i.role} invite`;
                      if (!window.confirm(`Delete "${who}"? This removes it permanently.`)) return;
                      try { await api.delete(`/invites/${i.id}`); toast.success("Deleted"); load(); }
                      catch (e) { toast.error(formatApiError(e)); }
                    }} data-testid={`invite-delete-${i.id}`}
                      className="text-xs font-mono uppercase px-2 py-1 border border-zinc-700 hover:bg-red-950/40 hover:border-red-500/40 hover:text-red-300 rounded-sm inline-flex items-center gap-1">
                      <Trash2 className="h-3 w-3" /> Delete
                    </button>
                  </td>
                </tr>
              );
            })}
            {invites.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-sm text-zinc-500">No invites yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AuditPanel() {
  const [entries, setEntries] = useState([]);
  const [filters, setFilters] = useState({ entity_type: "", username: "" });

  const load = async () => {
    const params = new URLSearchParams();
    if (filters.entity_type) params.set("entity_type", filters.entity_type);
    if (filters.username) params.set("username", filters.username);
    params.set("limit", "200");
    const { data } = await api.get(`/audit?${params.toString()}`);
    setEntries(data);
  };
  useEffect(() => { load(); }, [filters.entity_type, filters.username]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <select value={filters.entity_type} onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })} data-testid="audit-filter-entity"
          className="bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-1.5 font-mono text-xs outline-none">
          <option value="">All entities</option>
          <option value="fleet">Fleet</option>
          <option value="inventory">Inventory</option>
          <option value="user">User</option>
          <option value="invite">Invite</option>
        </select>
        <input placeholder="Filter by username" value={filters.username} onChange={(e) => setFilters({ ...filters, username: e.target.value })} data-testid="audit-filter-user"
          className="bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-1.5 font-mono text-xs outline-none" />
      </div>
      <div className="border border-zinc-800 rounded-sm overflow-hidden">
        <ul className="divide-y divide-zinc-800 max-h-[600px] overflow-y-auto">
          {entries.length === 0 && <li className="p-6 text-center text-sm text-zinc-500">No activity.</li>}
          {entries.map((e) => (
            <li key={e.id} className="px-4 py-2.5 text-sm" data-testid={`audit-entry-${e.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-zinc-100 font-medium">{e.username}</span>
                <span className="text-zinc-500">·</span>
                <span className="font-mono uppercase text-[10px] tracking-wider text-zinc-400 border border-zinc-800 px-1.5 py-0.5">{e.action}</span>
                <span className="font-mono uppercase text-[10px] tracking-wider text-red-300 border border-red-500/30 bg-red-500/10 px-1.5 py-0.5">{e.entity_type}</span>
                <span className="text-[10px] font-mono text-zinc-500 ml-auto">{fmtDate(e.timestamp)}</span>
              </div>
              {e.summary && <div className="text-xs text-zinc-400 mt-0.5">{e.summary}</div>}
              {e.field && (
                <div className="text-[11px] font-mono text-zinc-500 mt-0.5">
                  {e.field}: <span className="text-red-400 line-through">{String(e.old_value ?? "—")}</span> → <span className="text-emerald-400">{String(e.new_value ?? "—")}</span>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
